import type { ContractReview } from '@/services/contractReview.service';
import {
  formatContractReviewSectionTitle,
  getContractReviewVisibleSectionKeys,
  normalizeContractReviewSections,
} from '@/components/crm/contractReviewChecklistSections';

const FINAL_RECOMMENDATION_LABELS: Record<string, string> = {
  approved_to_convert: 'Approved to convert to Customer Order',
  approved_with_conditions: 'Approved with conditions',
  rejected_needs_correction: 'Rejected / needs correction',
  defer: 'Defer',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatIsoDate(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const d = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  try {
    const t = Date.parse(String(value));
    if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
  } catch {
    /* ignore */
  }
  return String(value);
}

function metaRow(label: string, valueHtml: string): string {
  return `<tr><th scope="row">${escapeHtml(label)}</th><td>${valueHtml}</td></tr>`;
}

function quotationLabel(row: ContractReview): string {
  const q = row.quotation;
  if (q?.quotation_number) {
    let s = escapeHtml(q.quotation_number);
    if (q.quotation_name) s += `<div class="sub">${escapeHtml(q.quotation_name)}</div>`;
    if (q.status) s += `<div class="sub muted">Quotation status: ${escapeHtml(q.status)}</div>`;
    return s;
  }
  return `<span class="muted">${escapeHtml(row.quotation_id)}</span>`;
}

export type ContractReviewPrintHtmlOptions = {
  revenueServiceTypeLabel?: string;
};

/** Full HTML document suitable for `document.write` + print. */
export function buildContractReviewPrintHtml(row: ContractReview, opts?: ContractReviewPrintHtmlOptions): string {
  const sections = normalizeContractReviewSections(row.sections as Record<string, unknown> | undefined);
  const printedAt = new Date().toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
  const finalRec = row.final_recommendation
    ? FINAL_RECOMMENDATION_LABELS[row.final_recommendation] ?? row.final_recommendation
    : null;

  const metaParts = [
    metaRow('Quotation', quotationLabel(row)),
    metaRow(
      'Revenue service type',
      opts?.revenueServiceTypeLabel && opts.revenueServiceTypeLabel !== '—'
        ? escapeHtml(opts.revenueServiceTypeLabel)
        : '—'
    ),
    metaRow('Review date', escapeHtml(formatIsoDate(row.review_date))),
    metaRow('Document status', escapeHtml(`${row.review_status} · ${row.approval_status}`)),
    metaRow('Overall risk rating', escapeHtml(row.overall_risk_rating?.trim() || '—')),
    metaRow('Final recommendation', escapeHtml(finalRec?.trim() || '—')),
    metaRow('Conditions', row.conditions_text?.trim() ? escapeHtml(row.conditions_text) : '—'),
    metaRow('Conditions acknowledged', row.conditions_acknowledged_at ? escapeHtml(String(row.conditions_acknowledged_at)) : '—'),
    metaRow('Approved at', row.approved_at ? escapeHtml(String(row.approved_at)) : '—'),
    metaRow('Prepared by', row.prepared_by?.trim() ? escapeHtml(row.prepared_by) : '—'),
    metaRow('Reviewed by', row.reviewed_by?.trim() ? escapeHtml(row.reviewed_by) : '—'),
    metaRow('Remarks', row.remarks?.trim() ? escapeHtml(row.remarks) : '—'),
  ];
  if (row.is_stale) {
    metaParts.push(metaRow('Stale vs quotation', escapeHtml(row.stale_reason || 'Review may be out of date')));
  }
  const metaHtml = metaParts.join('');

  let sectionsHtml = '';
  const sectionKeys = getContractReviewVisibleSectionKeys(sections);
  for (const key of sectionKeys) {
    const items = sections[key] || [];
    const rows = items
      .map(
        (item) =>
          `<tr><th scope="row">${escapeHtml(item.label || '—')}</th><td>${escapeHtml(item.response || '—')}</td><td>${
            item.remarks?.trim() ? escapeHtml(item.remarks) : '—'
          }</td></tr>`
      )
      .join('');
    if (!rows) continue;
    sectionsHtml += `
  <section class="cr-section">
    <h2>${escapeHtml(formatContractReviewSectionTitle(key))}</h2>
    <table class="cr-grid">
      <thead><tr><th>Item</th><th>Response</th><th>Remarks</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
  }

  const title = escapeHtml(`Contract Review · ${row.review_number}`);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(row.review_number)}</title>
  <style>
    :root {
      --border: #c5cdd5;
      --muted: #5c6b7a;
      --heading: #1a2430;
    }
    * { box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.45;
      color: #222;
      margin: 0;
      padding: 12mm 14mm;
      background: #fff;
    }
    .cr-doc-header {
      border-bottom: 2px solid var(--heading);
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .cr-doc-header h1 {
      margin: 0 0 4px 0;
      font-size: 18pt;
      font-weight: 600;
      color: var(--heading);
      letter-spacing: -0.02em;
    }
    .cr-doc-header .cr-sub {
      font-size: 10pt;
      color: var(--muted);
    }
    table.cr-meta {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      font-size: 10pt;
    }
    table.cr-meta th {
      text-align: left;
      font-weight: 600;
      width: 28%;
      padding: 6px 10px 6px 0;
      vertical-align: top;
      color: var(--heading);
      border-bottom: 1px solid #e8ecf0;
    }
    table.cr-meta td {
      padding: 6px 0;
      vertical-align: top;
      border-bottom: 1px solid #e8ecf0;
    }
    .sub { font-size: 9.5pt; margin-top: 2px; }
    .muted { color: var(--muted); font-size: 9.5pt; }
    section.cr-section {
      margin-bottom: 18px;
      page-break-inside: avoid;
    }
    section.cr-section h2 {
      margin: 0 0 8px 0;
      font-size: 10pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--heading);
      border-bottom: 1px solid var(--border);
      padding-bottom: 4px;
    }
    table.cr-grid {
      width: 100%;
      border-collapse: collapse;
      font-size: 10pt;
    }
    table.cr-grid th, table.cr-grid td {
      border: 1px solid var(--border);
      padding: 6px 8px;
      vertical-align: top;
      text-align: left;
    }
    table.cr-grid thead th {
      background: #f4f6f8;
      font-weight: 600;
      width: auto;
    }
    table.cr-grid tbody th { width: 42%; font-weight: 500; background: #fafbfc; }
    .cr-footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid var(--border);
      font-size: 9pt;
      color: var(--muted);
    }
    @media print {
      body { padding: 8mm 10mm; }
      section.cr-section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <header class="cr-doc-header">
    <h1>${title}</h1>
    <div class="cr-sub">Contract review checklist · printed ${escapeHtml(printedAt)}</div>
  </header>
  <table class="cr-meta">${metaHtml}</table>
  ${sectionsHtml}
  <footer class="cr-footer">Generated from Contract Review register · ${escapeHtml(row.review_number)}</footer>
</body>
</html>`;
}
